import React from 'react';
import { Target, DollarSign, Brain, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GOAL_CONFIG = {
  monthly_profit: {
    icon: DollarSign,
    label: 'Monthly Profit',
    format: (val) => `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    formatTarget: (val) => `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    inverted: false,
  },
  reduce_emotional: {
    icon: Brain,
    label: 'Reduce Emotional Trades',
    format: (val) => String(Math.round(val)),
    formatTarget: (val) => `${val} max`,
    inverted: true,
  },
  journal_consistency: {
    icon: BookOpen,
    label: 'Journal Consistency',
    format: (val) => String(Math.round(val)),
    formatTarget: (val) => `${val} days`,
    inverted: false,
  },
};

const getProgressColor = (percentage, inverted) => {
  const effective = inverted ? percentage : percentage;
  if (effective >= 80) return 'bg-green-500';
  if (effective >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const GoalTracker = ({ goals = [], progress = [] }) => {
  const navigate = useNavigate();

  // Merge goals with progress data
  const mergedGoals = goals.map((goal) => {
    const prog = progress.find((p) => p.goal_type === goal.goal_type);
    return {
      ...goal,
      current_value: prog?.current_value ?? 0,
      percentage: prog?.percentage ?? 0,
    };
  });

  const hasGoals = mergedGoals.length > 0;

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Goal Tracker</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Monthly progress</p>
        </div>
        <button
          onClick={() => navigate('/profile')}
          className="text-xs text-primary hover:text-primary/80 transition-colors"
        >
          Edit Goals
        </button>
      </div>

      {!hasGoals ? (
        <div className="flex flex-col items-center justify-center h-32 gap-2">
          <Target className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No goals set yet.</p>
          <button
            onClick={() => navigate('/profile')}
            className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Set Goals in Profile
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {mergedGoals.map((goal) => {
            const config = GOAL_CONFIG[goal.goal_type];
            if (!config) return null;
            const Icon = config.icon;
            const barWidth = Math.min(100, Math.max(0, goal.percentage));

            return (
              <div key={goal.goal_type}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {config.label}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {config.format(goal.current_value)} / {config.formatTarget(goal.target_value)}
                  </span>
                </div>
                <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getProgressColor(goal.percentage, config.inverted)}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <div className="text-right mt-0.5">
                  <span className="text-[10px] text-muted-foreground">{goal.percentage}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalTracker;
